/**
 * useBuildingDragInteraction - Hook for managing mouse AND touch interactions
 * in building editor
 *
 * Handles:
 * - Building selection via click/tap
 * - Dragging buildings with map pan disabled (mouse / touch)
 * - Rotation via ring interaction (mouse / touch)
 * - Two-finger twist rotation (touch only — replaces Shift+Scroll on desktop)
 * - Hover states for cursor feedback (mouse only)
 *
 * Touch gestures:
 * - Tap in add mode (not placed) -> place building
 * - Drag building body (add/edit mode, placed) -> move
 * - Drag rotation ring -> rotate
 * - Two-finger twist on building -> rotate
 * - Tap empty area -> map pan (native)
 *
 * Uses the demo app's pattern with:
 * - getState() calls for fresh state inside event handlers
 * - Closure variables for drag state
 * - Minimal effect dependencies to prevent listener re-attachment
 * - Mapbox's unproject() for reliable screen-to-world coordinate conversion
 */

import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import { useBuildingEditorStore } from '@/stores/buildingEditorStore'
import { useObstacleStore } from '@/stores/obstacleStore'
import { screenToWorldPosition } from '@/utils/coordinates'
import { FEET_TO_METERS, METERS_TO_FEET } from '@/constants/units'

interface UseBuildingDragInteractionParams {
  map: mapboxgl.Map | null
  sceneObjects: {
    scene: THREE.Scene | null
    camera: THREE.Camera | null
    buildings: THREE.Group[]
    groundPlane: THREE.Mesh | null
    rotationRing: THREE.Group | null
  } | null
  enabled: boolean
}

// Touch-specific constants
/** Additional tolerance for touch ring hit detection (meters) */
const TOUCH_RING_TOLERANCE_EXTRA = 3
/** Additional hit radius padding for touch (meters) */
const TOUCH_HIT_PADDING_EXTRA = 3
/** Cooldown to suppress synthesized mouse events after touch (ms) */
const TOUCH_MOUSE_GUARD_MS = 500

export function useBuildingDragInteraction({
  map,
  sceneObjects,
  enabled,
}: UseBuildingDragInteractionParams) {
  // ========================================
  // REFS — Shared state
  // ========================================
  const isDraggingRef = useRef(false)
  const isRotatingRingRef = useRef(false)

  // Click/tap detection
  const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null)
  const mouseDownTimeRef = useRef<number>(0)
  const wasDragRef = useRef(false)

  // Ring rotation delta tracking
  const ringDragStartAngleRef = useRef<number | null>(null)
  const ringDragStartRotationRef = useRef<number | null>(null)

  // ========================================
  // REFS — Touch-specific
  // ========================================
  /** True when in two-finger twist rotation */
  const isTwoFingerTwistRef = useRef(false)
  /** Screen-space angle between two fingers at twist start */
  const twoFingerStartAngleRef = useRef<number | null>(null)
  /** Building rotation at twist start */
  const twoFingerStartRotationRef = useRef<number | null>(null)
  /** Timestamp of last touch event — suppresses synthesized mouse events */
  const lastTouchTimeRef = useRef(0)

  useEffect(() => {
    if (!map || !sceneObjects || !enabled) return

    const canvas = map.getCanvas()

    // ========================================
    // HELPER FUNCTIONS
    // ========================================

    const getWorldPosition = (mouse: THREE.Vector2): THREE.Vector3 | null => {
      const { mapCenter } = useBuildingEditorStore.getState()
      if (!mapCenter) {
        const center = map.getCenter()
        return screenToWorldPosition(mouse, map, [center.lng, center.lat])
      }
      const modelOrigin: [number, number] = [mapCenter.lng, mapCenter.lat]
      return screenToWorldPosition(mouse, map, modelOrigin)
    }

    const getWorldFromScreen = (screenX: number, screenY: number): THREE.Vector3 | null => {
      const mouse = new THREE.Vector2(
        (screenX / canvas.clientWidth) * 2 - 1,
        -(screenY / canvas.clientHeight) * 2 + 1
      )
      return getWorldPosition(mouse)
    }

    const getPreviewBuildingWorldPos = (): {
      x: number
      z: number
      previewBuilding: ReturnType<typeof useBuildingEditorStore.getState>['previewBuilding']
    } | null => {
      const { previewBuilding } = useBuildingEditorStore.getState()
      if (!previewBuilding) return null
      const buildingX = previewBuilding.worldX * FEET_TO_METERS
      const buildingZ = previewBuilding.worldZ * FEET_TO_METERS
      return { x: buildingX, z: buildingZ, previewBuilding }
    }

    /**
     * @param extraTolerance Additional tolerance in meters (used for touch targets)
     */
    const isOverRotationRing = (screenX: number, screenY: number, extraTolerance = 0): boolean => {
      const { mode, previewBuilding } = useBuildingEditorStore.getState()
      if (mode === 'view') return false
      if (!previewBuilding) return false

      const mouseWorldPos = getWorldFromScreen(screenX, screenY)
      if (!mouseWorldPos) return false

      const buildingWorldPos = getPreviewBuildingWorldPos()
      if (!buildingWorldPos) return false

      const dx = mouseWorldPos.x - buildingWorldPos.x
      const dz = mouseWorldPos.z - buildingWorldPos.z
      const distWorld = Math.sqrt(dx * dx + dz * dz)

      const RING_RADIUS_METERS = 20
      const RING_TOLERANCE_METERS = 2
      // Asymmetric: tight inward, generous outward
      const innerTolerance = RING_TOLERANCE_METERS + extraTolerance * 0.3
      const outerTolerance = RING_TOLERANCE_METERS + extraTolerance

      return distWorld >= RING_RADIUS_METERS - innerTolerance && distWorld <= RING_RADIUS_METERS + outerTolerance
    }

    /**
     * @param extraPadding Additional hit radius in meters (used for touch targets)
     */
    const isOverPreviewBuilding = (screenX: number, screenY: number, extraPadding = 0): boolean => {
      const { mode, previewBuilding } = useBuildingEditorStore.getState()
      if (mode === 'view' || !previewBuilding) return false

      const mouseWorldPos = getWorldFromScreen(screenX, screenY)
      if (!mouseWorldPos) return false

      const buildingWorldPos = getPreviewBuildingWorldPos()
      if (!buildingWorldPos) return false

      // Transform click into building-local frame (OBB check)
      const dx = mouseWorldPos.x - buildingWorldPos.x
      const dz = mouseWorldPos.z - buildingWorldPos.z
      const rad = ((previewBuilding.bearing_deg || 0) * Math.PI) / 180
      const cos = Math.cos(rad)
      const sin = Math.sin(rad)
      const localX = dx * cos + dz * sin
      const localZ = -dx * sin + dz * cos

      const halfW = (previewBuilding.width_ft / 2) * FEET_TO_METERS + 2 + extraPadding
      const halfD = (previewBuilding.depth_ft / 2) * FEET_TO_METERS + 2 + extraPadding

      return Math.abs(localX) <= halfW && Math.abs(localZ) <= halfD
    }

    const getBuildingAtPosition = (mouse: THREE.Vector2): THREE.Group | null => {
      if (!sceneObjects.buildings || sceneObjects.buildings.length === 0) return null

      const worldPos = getWorldPosition(mouse)
      if (!worldPos) return null

      for (const building of sceneObjects.buildings) {
        if (building.name === 'preview-building') continue
        building.updateMatrixWorld(true)

        let isInsideMesh = false
        building.traverse((child: THREE.Object3D) => {
          if (child instanceof THREE.Mesh && child.geometry) {
            const geometry = child.geometry
            if (!geometry.boundingBox) geometry.computeBoundingBox()
            if (geometry.boundingBox) {
              const meshBox = geometry.boundingBox.clone()
              meshBox.applyMatrix4(child.matrixWorld)
              if (
                worldPos.x >= meshBox.min.x &&
                worldPos.x <= meshBox.max.x &&
                worldPos.z >= meshBox.min.z &&
                worldPos.z <= meshBox.max.z
              ) {
                isInsideMesh = true
              }
            }
          }
        })

        if (isInsideMesh) return building
      }

      return null
    }

    // ========================================
    // SHARED INTERACTION LOGIC
    // ========================================

    const enableMapControls = () => {
      map.dragPan.enable()
      map.dragRotate.enable()
      map.touchZoomRotate?.enable()
    }

    const disableMapControls = () => {
      map.dragPan.disable()
      map.dragRotate.disable()
      map.touchZoomRotate?.disable()
    }

    // ========================================
    // MOUSE EVENT HANDLERS
    // ========================================

    const handleMouseDown = (e: mapboxgl.MapMouseEvent) => {
      if (Date.now() - lastTouchTimeRef.current < TOUCH_MOUSE_GUARD_MS) return
      if (e.originalEvent.button !== 0) return

      // Safety net: yield to obstacle interactions
      const obstacleState = useObstacleStore.getState()
      if (obstacleState.isPlacingMode || obstacleState.isDragging) return

      mouseDownPosRef.current = { x: e.point.x, y: e.point.y }
      mouseDownTimeRef.current = Date.now()
      wasDragRef.current = false

      const { mode, isPlaced, setDragging, setRotating } =
        useBuildingEditorStore.getState()

      if (mode === 'add' && !isPlaced) return

      const isOverBuilding = isOverPreviewBuilding(e.point.x, e.point.y)
      const isOverRing_ = isOverRotationRing(e.point.x, e.point.y)

      if ((mode === 'add' || mode === 'edit') && isOverRing_) {
        isRotatingRingRef.current = true
        e.preventDefault()
        map.dragPan.disable()
        map.dragRotate.disable()
        setRotating(true)
        // Capture start angle for delta rotation
        const worldPos = getWorldFromScreen(e.point.x, e.point.y)
        const buildingPos = getPreviewBuildingWorldPos()
        if (worldPos && buildingPos) {
          ringDragStartAngleRef.current = Math.atan2(worldPos.x - buildingPos.x, worldPos.z - buildingPos.z) * 180 / Math.PI
          ringDragStartRotationRef.current = buildingPos.previewBuilding?.bearing_deg || 0
        }
        canvas.style.cursor = 'grabbing'
        console.log('Building rotation started')
        return
      }

      if ((mode === 'add' || mode === 'edit') && isOverBuilding) {
        isDraggingRef.current = true
        e.preventDefault()
        map.dragPan.disable()
        map.dragRotate.disable()
        setDragging(true)
        canvas.style.cursor = 'move'
        console.log('Building drag started')
        return
      }
    }

    const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
      if (Date.now() - lastTouchTimeRef.current < TOUCH_MOUSE_GUARD_MS) return

      if (mouseDownPosRef.current) {
        const dx = e.point.x - mouseDownPosRef.current.x
        const dy = e.point.y - mouseDownPosRef.current.y
        if (Math.sqrt(dx * dx + dy * dy) > 5) {
          wasDragRef.current = true
        }
      }

      const mouse = new THREE.Vector2(
        (e.point.x / canvas.clientWidth) * 2 - 1,
        -(e.point.y / canvas.clientHeight) * 2 + 1
      )

      const {
        mode,
        isPlaced,
        previewBuilding,
        updatePreviewWorldPosition,
        updatePreviewRotation,
        setHoveredBuilding,
      } = useBuildingEditorStore.getState()

      // Add mode (not placed): preview follows cursor
      if (mode === 'add' && !isPlaced && previewBuilding) {
        const worldPos = getWorldPosition(mouse)
        if (worldPos) {
          updatePreviewWorldPosition(worldPos.x * METERS_TO_FEET, worldPos.z * METERS_TO_FEET)
        }
        canvas.style.cursor = 'crosshair'
        return
      }

      // Rotation (delta-based)
      if (isRotatingRingRef.current && previewBuilding) {
        const worldPos = getWorldPosition(mouse)
        if (worldPos && ringDragStartAngleRef.current !== null) {
          const buildingPos = getPreviewBuildingWorldPos()
          if (buildingPos) {
            const currentAngle = Math.atan2(worldPos.x - buildingPos.x, worldPos.z - buildingPos.z) * 180 / Math.PI
            const delta = currentAngle - ringDragStartAngleRef.current
            let newBearing = (ringDragStartRotationRef.current ?? 0) + delta
            newBearing = ((newBearing % 360) + 360) % 360
            updatePreviewRotation(newBearing)
          }
        }
        canvas.style.cursor = 'grabbing'
        return
      }

      // Drag
      if (isDraggingRef.current && previewBuilding) {
        const worldPos = getWorldPosition(mouse)
        if (worldPos) {
          updatePreviewWorldPosition(worldPos.x * METERS_TO_FEET, worldPos.z * METERS_TO_FEET)
        }
        canvas.style.cursor = 'move'
        return
      }

      // Hover (mouse only)
      if (!isDraggingRef.current && !isRotatingRingRef.current) {
        if ((mode === 'add' || mode === 'edit') && isOverRotationRing(e.point.x, e.point.y)) {
          canvas.style.cursor = 'grab'
          return
        }
        if ((mode === 'add' || mode === 'edit') && isOverPreviewBuilding(e.point.x, e.point.y)) {
          canvas.style.cursor = 'move'
          return
        }
        const building = getBuildingAtPosition(mouse)
        if (building) {
          const buildingId = building.userData.buildingId
          setHoveredBuilding(buildingId || null)
          canvas.style.cursor = 'pointer'
        } else {
          setHoveredBuilding(null)
          canvas.style.cursor = 'default'
        }
      }
    }

    const handleMouseUp = (e: mapboxgl.MapMouseEvent) => {
      if (Date.now() - lastTouchTimeRef.current < TOUCH_MOUSE_GUARD_MS) return

      const { mode, isPlaced, setIsPlaced, setDragging, setRotating } =
        useBuildingEditorStore.getState()

      // Placement: genuine click in add mode
      if (mode === 'add' && !isPlaced && mouseDownPosRef.current) {
        const duration = Date.now() - mouseDownTimeRef.current
        const dx = e.point.x - mouseDownPosRef.current.x
        const dy = e.point.y - mouseDownPosRef.current.y
        const distance = Math.sqrt(dx * dx + dy * dy)
        const isClick = duration < 300 && distance < 10

        if (isClick && !wasDragRef.current) {
          setIsPlaced(true)
          canvas.style.cursor = 'move'
          console.log('Building placed')
        }
      }

      mouseDownPosRef.current = null
      mouseDownTimeRef.current = 0
      wasDragRef.current = false

      if (isDraggingRef.current || isRotatingRingRef.current) {
        isDraggingRef.current = false
        isRotatingRingRef.current = false
        ringDragStartAngleRef.current = null
        ringDragStartRotationRef.current = null
        map.dragPan.enable()
        map.dragRotate.enable()
        setDragging(false)
        setRotating(false)
        canvas.style.cursor = 'default'
        console.log('Building drag/rotation ended')
      }
    }

    const handleMouseOut = () => {
      if (Date.now() - lastTouchTimeRef.current < TOUCH_MOUSE_GUARD_MS) return

      if (isDraggingRef.current || isRotatingRingRef.current) {
        isDraggingRef.current = false
        isRotatingRingRef.current = false
        ringDragStartAngleRef.current = null
        ringDragStartRotationRef.current = null
        map.dragPan.enable()
        map.dragRotate.enable()
        const { setDragging, setRotating, setHoveredBuilding } =
          useBuildingEditorStore.getState()
        setDragging(false)
        setRotating(false)
        setHoveredBuilding(null)
        console.log('Mouse left map - building drag cancelled')
      }
      canvas.style.cursor = 'default'
    }

    // ========================================
    // TOUCH EVENT HANDLERS
    // ========================================

    const handleTouchStart = (e: mapboxgl.MapTouchEvent) => {
      lastTouchTimeRef.current = Date.now()
      const touches = e.originalEvent.touches

      // --- Multi-touch (2+ fingers) ---
      if (touches.length >= 2) {
        // Cancel any in-progress 1-finger drag
        if (isDraggingRef.current) {
          isDraggingRef.current = false
          useBuildingEditorStore.getState().setDragging(false)
        }

        // Start two-finger twist if in add/edit mode with preview building
        const { mode, previewBuilding } = useBuildingEditorStore.getState()
        if ((mode === 'add' || mode === 'edit') && previewBuilding && e.points.length >= 2) {
          isTwoFingerTwistRef.current = true
          twoFingerStartAngleRef.current = Math.atan2(
            e.points[1].y - e.points[0].y,
            e.points[1].x - e.points[0].x
          )
          twoFingerStartRotationRef.current = previewBuilding.bearing_deg || 0

          e.preventDefault()
          disableMapControls()
          useBuildingEditorStore.getState().setRotating(true)
          console.log('Touch: two-finger twist started')
        }
        return
      }

      // --- Single touch ---
      mouseDownPosRef.current = { x: e.point.x, y: e.point.y }
      mouseDownTimeRef.current = Date.now()
      wasDragRef.current = false

      const { mode, isPlaced, setDragging, setRotating } =
        useBuildingEditorStore.getState()

      // Add mode (not placed): don't start interaction, preview follows touchmove
      if (mode === 'add' && !isPlaced) return

      // Check rotation ring (wider tolerance for touch)
      if ((mode === 'add' || mode === 'edit') && isOverRotationRing(e.point.x, e.point.y, TOUCH_RING_TOLERANCE_EXTRA)) {
        isRotatingRingRef.current = true
        e.preventDefault()
        disableMapControls()
        setRotating(true)
        // Capture start angle for delta rotation
        const worldPos = getWorldFromScreen(e.point.x, e.point.y)
        const buildingPos = getPreviewBuildingWorldPos()
        if (worldPos && buildingPos) {
          ringDragStartAngleRef.current = Math.atan2(worldPos.x - buildingPos.x, worldPos.z - buildingPos.z) * 180 / Math.PI
          ringDragStartRotationRef.current = buildingPos.previewBuilding?.bearing_deg || 0
        }
        console.log('Touch: building rotation started')
        return
      }

      // Check building hit (wider target for touch)
      if ((mode === 'add' || mode === 'edit') && isOverPreviewBuilding(e.point.x, e.point.y, TOUCH_HIT_PADDING_EXTRA)) {
        isDraggingRef.current = true
        e.preventDefault()
        disableMapControls()
        setDragging(true)
        console.log('Touch: building drag started')
        return
      }
    }

    const handleTouchMove = (e: mapboxgl.MapTouchEvent) => {
      lastTouchTimeRef.current = Date.now()

      // --- Two-finger twist ---
      if (isTwoFingerTwistRef.current && e.points.length >= 2) {
        const { previewBuilding, updatePreviewRotation } = useBuildingEditorStore.getState()
        if (!previewBuilding) return

        const currentAngle = Math.atan2(
          e.points[1].y - e.points[0].y,
          e.points[1].x - e.points[0].x
        )
        const deltaAngle = currentAngle - (twoFingerStartAngleRef.current ?? 0)
        const deltaDegrees = (deltaAngle * 180) / Math.PI
        let newRotation = (twoFingerStartRotationRef.current ?? 0) - deltaDegrees
        newRotation = ((newRotation % 360) + 360) % 360
        updatePreviewRotation(newRotation)
        return
      }

      // Track drag distance for tap detection
      if (mouseDownPosRef.current) {
        const dx = e.point.x - mouseDownPosRef.current.x
        const dy = e.point.y - mouseDownPosRef.current.y
        if (Math.sqrt(dx * dx + dy * dy) > 8) {
          wasDragRef.current = true
        }
      }

      const mouse = new THREE.Vector2(
        (e.point.x / canvas.clientWidth) * 2 - 1,
        -(e.point.y / canvas.clientHeight) * 2 + 1
      )

      const {
        mode,
        isPlaced,
        previewBuilding,
        updatePreviewWorldPosition,
        updatePreviewRotation,
      } = useBuildingEditorStore.getState()

      // Add mode (not placed): preview follows finger
      if (mode === 'add' && !isPlaced && previewBuilding) {
        const worldPos = getWorldPosition(mouse)
        if (worldPos) {
          updatePreviewWorldPosition(worldPos.x * METERS_TO_FEET, worldPos.z * METERS_TO_FEET)
        }
        return
      }

      // Rotation (ring drag, delta-based)
      if (isRotatingRingRef.current && previewBuilding) {
        const worldPos = getWorldPosition(mouse)
        if (worldPos && ringDragStartAngleRef.current !== null) {
          const buildingPos = getPreviewBuildingWorldPos()
          if (buildingPos) {
            const currentAngle = Math.atan2(worldPos.x - buildingPos.x, worldPos.z - buildingPos.z) * 180 / Math.PI
            const delta = currentAngle - ringDragStartAngleRef.current
            let newBearing = (ringDragStartRotationRef.current ?? 0) + delta
            newBearing = ((newBearing % 360) + 360) % 360
            updatePreviewRotation(newBearing)
          }
        }
        return
      }

      // Drag
      if (isDraggingRef.current && previewBuilding) {
        const worldPos = getWorldPosition(mouse)
        if (worldPos) {
          updatePreviewWorldPosition(worldPos.x * METERS_TO_FEET, worldPos.z * METERS_TO_FEET)
        }
        return
      }
    }

    const handleTouchEnd = (e: mapboxgl.MapTouchEvent) => {
      lastTouchTimeRef.current = Date.now()

      const { mode, isPlaced, setIsPlaced, setDragging, setRotating } =
        useBuildingEditorStore.getState()

      // Placement: genuine tap in add mode (not placed)
      if (mode === 'add' && !isPlaced && mouseDownPosRef.current) {
        const duration = Date.now() - mouseDownTimeRef.current
        const dx = e.point.x - mouseDownPosRef.current.x
        const dy = e.point.y - mouseDownPosRef.current.y
        const distance = Math.sqrt(dx * dx + dy * dy)
        const isTap = duration < 300 && distance < 15 // Slightly larger for touch

        if (isTap && !wasDragRef.current) {
          setIsPlaced(true)
          console.log('Touch: building placed')
        }
      }

      mouseDownPosRef.current = null
      mouseDownTimeRef.current = 0
      wasDragRef.current = false

      const wasInteracting = isDraggingRef.current || isRotatingRingRef.current || isTwoFingerTwistRef.current

      isDraggingRef.current = false
      isRotatingRingRef.current = false
      ringDragStartAngleRef.current = null
      ringDragStartRotationRef.current = null
      isTwoFingerTwistRef.current = false
      twoFingerStartAngleRef.current = null
      twoFingerStartRotationRef.current = null

      if (wasInteracting) {
        enableMapControls()
        setDragging(false)
        setRotating(false)
        console.log('Touch: building interaction ended')
      }
    }

    const handleTouchCancel = () => {
      lastTouchTimeRef.current = Date.now()

      isDraggingRef.current = false
      isRotatingRingRef.current = false
      ringDragStartAngleRef.current = null
      ringDragStartRotationRef.current = null
      isTwoFingerTwistRef.current = false
      twoFingerStartAngleRef.current = null
      twoFingerStartRotationRef.current = null
      mouseDownPosRef.current = null
      mouseDownTimeRef.current = 0
      wasDragRef.current = false

      enableMapControls()

      const { setDragging, setRotating, setHoveredBuilding } =
        useBuildingEditorStore.getState()
      setDragging(false)
      setRotating(false)
      setHoveredBuilding(null)
      console.log('Touch cancelled — building interaction reset')
    }

    // ========================================
    // WHEEL HANDLER — Shift + Scroll for rotation (desktop only)
    // ========================================
    const handleWheel = (e: WheelEvent) => {
      const { mode,  previewBuilding, updatePreviewRotation } =
        useBuildingEditorStore.getState()

      if (e.shiftKey && previewBuilding) {
        if (mode === 'add' || mode === 'edit') {
          e.preventDefault()
          e.stopPropagation()
          const delta = e.deltaY > 0 ? -5 : 5
          const currentBearing = previewBuilding.bearing_deg || 0
          const newBearing = ((currentBearing + delta) % 360 + 360) % 360
          updatePreviewRotation(newBearing)
        }
      }
    }

    // ========================================
    // KEYBOARD HANDLER — Escape to cancel
    // ========================================
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const { mode, cancelAddBuilding, cancelEdit } =
          useBuildingEditorStore.getState()
        if (mode === 'add') {
          cancelAddBuilding()
          console.log('Add mode cancelled via Escape')
        } else if (mode === 'edit') {
          cancelEdit()
          console.log('Edit mode cancelled via Escape')
        }
      }
    }

    // ========================================
    // ATTACH EVENT LISTENERS
    // ========================================
    console.log('Attaching building event listeners (mouse + touch)')
    map.on('mousedown', handleMouseDown)
    map.on('mousemove', handleMouseMove)
    map.on('mouseup', handleMouseUp)
    map.on('mouseout', handleMouseOut)
    map.on('touchstart', handleTouchStart)
    map.on('touchmove', handleTouchMove)
    map.on('touchend', handleTouchEnd)
    map.on('touchcancel', handleTouchCancel)

    canvas.addEventListener('wheel', handleWheel, { passive: false })
    document.addEventListener('keydown', handleKeyDown)

    // Cleanup
    return () => {
      console.log('Removing building event listeners')

      if (isDraggingRef.current || isRotatingRingRef.current || isTwoFingerTwistRef.current) {
        isDraggingRef.current = false
        isRotatingRingRef.current = false
        ringDragStartAngleRef.current = null
        ringDragStartRotationRef.current = null
        isTwoFingerTwistRef.current = false
        twoFingerStartAngleRef.current = null
        twoFingerStartRotationRef.current = null
        enableMapControls()
      }

      map.off('mousedown', handleMouseDown)
      map.off('mousemove', handleMouseMove)
      map.off('mouseup', handleMouseUp)
      map.off('mouseout', handleMouseOut)
      map.off('touchstart', handleTouchStart)
      map.off('touchmove', handleTouchMove)
      map.off('touchend', handleTouchEnd)
      map.off('touchcancel', handleTouchCancel)

      canvas.removeEventListener('wheel', handleWheel)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [map, enabled])
}
